import type { Locale } from '@/brand/i18n';

/**
 * Admin console copy.
 *
 * Operational Arabic: the vocabulary an Egyptian retail operations team already uses —
 * «مرتجعات», «مخزون», «فاتورة», «تدقيق» — rather than literal renderings of the English.
 * Status codes and template names stay in Latin because they are identifiers the
 * platform stores and an operator will search or quote verbatim.
 */
export interface Dictionary {
  app: { name: string; signIn: string; signOut: string; language: string };
  auth: {
    title: string; subtitle: string; email: string; password: string;
    showPassword: string; hidePassword: string; signingIn: string;
    devAccounts: string; devNote: string; failed: string;
    heroTitle: string; heroBody: string; arabicTagline: string;
  };
  nav: {
    overview: string; dashboard: string; analytics: string;
    simulator: string; products: string; customers: string; orders: string;
    invoices: string; payments: string; returns: string;
    automation: string; agent: string; connectors: string;
    governance: string; users: string; audit: string; system: string;
    sections: string; open: string; close: string;
  };
  common: {
    refresh: string; refreshNow: string; retry: string; loading: string; search: string;
    previous: string; next: string; pageOf: string; records: string; all: string;
    nothingToShow: string; readOnly: string; unavailable: string; errorTitle: string;
    restoringSession: string; noResults: string; adjustFilters: string;
  };
  dashboard: {
    title: string; subtitle: string;
    totalSales: string; invoicesCount: string; averageOrderValue: string;
    paymentSuccess: string; ofAttempts: string; returnRate: string; refunded: string;
    onlineSales: string; branchSales: string; activeCustomers: string; newCustomers: string;
    agentOrdersHour: string; lastRun: string;
    dailySales: string; channelMix: string; salesByBranch: string; bestProducts: string;
    lowStock: string; platformActivity: string;
    noSales: string; noRevenue: string; noBranchSales: string;
    product: string; units: string; revenue: string; warehouse: string;
    available: string; reorderAt: string; lines: string;
    runsCompleted: string; runsFailed: string; runsLastHour: string;
    aiAnswers: string; aiLatency: string; aiVerified: string;
    noModelWarning: string; autoRefresh: string;
  };
  analytics: {
    title: string; subtitle: string;
    range7: string; range30: string; range90: string; range365: string;
    vatCollected: string; discountsGiven: string; ordersPlaced: string;
    cancelled: string; paymentFailures: string; customersBought: string; ofActive: string;
    revenueOverTime: string; branchPerformance: string; returnsByReason: string;
    highestValue: string; noReturns: string; noPeriodSales: string;
    branch: string; invoicesCol: string; sales: string; avgOrder: string;
    customer: string; type: string; city: string; lifetimeValue: string;
  };
  agent: {
    title: string; subtitle: string; startRun: string; noScenario: string;
    runsCompleted: string; runsFailed: string; ordersLast50: string; valueGenerated: string;
    schedulerDisabled: string; configurations: string; recentRuns: string;
    name: string; schedule: string; scenario: string; lastRun: string;
    run: string; status: string; orders: string; value: string;
    decisionLog: string; decisionLogPrompt: string; decisionLogEmpty: string; decisionLogHint: string;
    loadingDecisions: string; stopRun: string; retryRun: string; actionFailed: string;
    default: string; unreachable: string;
  };
  connectors: {
    title: string; subtitle: string; unreachable: string;
    testConnection: string; testing: string; syncSchema: string; syncing: string;
    lastTested: string; lastSynced: string;
    noneTitle: string; noneBody: string; actionFailed: string;
    allowlistTitle: string; allowlistNote: string; personalFiltered: string;
    auditTitle: string; query: string; recordsCol: string; outcome: string; when: string;
  };
  records: {
    products: string; productsSubtitle: string;
    customers: string; customersSubtitle: string;
    orders: string; ordersSubtitle: string;
    invoices: string; invoicesSubtitle: string;
    payments: string; paymentsSubtitle: string;
    returns: string; returnsSubtitle: string;
    users: string; usersSubtitle: string;
    audit: string; auditSubtitle: string;
    colProduct: string; colCategory: string; colPrice: string; colStock: string; colStatus: string;
    colCustomer: string; colType: string; colCity: string; colPersona: string; colCreated: string;
    colOrder: string; colChannel: string; colTotal: string; colPlaced: string;
    colInvoice: string; colIssued: string; colReference: string; colMethod: string;
    colAmount: string; colRefunded: string; colWhen: string;
    colReturn: string; colReason: string; colRefund: string; colRaised: string;
    colUser: string; colRoles: string; colLastSignIn: string;
    colAction: string; colEntity: string; colActor: string; colRequest: string;
    active: string; inactive: string; disabled: string; synthetic: string;
    mustChangePassword: string; agentGenerated: string; agentRun: string; system: string;
    filterStatus: string; filterChannel: string; filterType: string; filterMethod: string;
    filterEntity: string;
  };
  health: {
    title: string; subtitle: string; healthy: string; degraded: string; unreachable: string;
    roundTrip: string; uptime: string; environment: string; noResponse: string;
    reprobed: string; probing: string; up: string; down: string;
    units: { second: string; minute: string; hour: string; day: string };
  };
}

const en: Dictionary = {
  app: { name: 'Nuat Admin', signIn: 'Sign in', signOut: 'Sign out', language: 'Language' },
  auth: {
    title: 'Sign in',
    subtitle: 'Use your Nuat platform account.',
    email: 'Email',
    password: 'Password',
    showPassword: 'Show password',
    hidePassword: 'Hide password',
    signingIn: 'Signing in…',
    devAccounts: 'Development accounts',
    devNote: 'Shown because this app is running on localhost.',
    failed: 'Sign-in failed',
    heroTitle: 'The nucleus of your company’s data',
    heroBody:
      'Open where it should be, protected where it must be. Every figure the platform ' +
      'reports traces back to the record it came from.',
    arabicTagline: 'نواة بياناتك',
  },
  nav: {
    overview: 'Overview', dashboard: 'Dashboard', analytics: 'Analytics',
    simulator: 'Simulator', products: 'Products', customers: 'Customers', orders: 'Orders',
    invoices: 'Invoices', payments: 'Payments', returns: 'Returns',
    automation: 'Automation & data', agent: 'Shopping Agent', connectors: 'Data Connectors',
    governance: 'Governance', users: 'Users & roles', audit: 'Audit logs', system: 'System health',
    sections: 'Sections', open: 'Open navigation', close: 'Close navigation',
  },
  common: {
    refresh: 'Refresh', refreshNow: 'Refresh now', retry: 'Try again', loading: 'Loading',
    search: 'Search…', previous: 'Previous', next: 'Next', pageOf: 'Page {page} of {total}',
    records: '{count} record(s)', all: 'all', nothingToShow: 'Nothing to show.',
    readOnly: 'Read-only — you lack {permission}', unavailable: 'unavailable',
    errorTitle: 'Something went wrong', restoringSession: 'Restoring session',
    noResults: 'No {entity} found',
    adjustFilters: 'Adjust the filters, or start an agent run to generate activity.',
  },
  dashboard: {
    title: 'Executive dashboard',
    subtitle: 'Last 30 days · {from} to {to}',
    totalSales: 'Total sales', invoicesCount: '{count} invoices',
    averageOrderValue: 'Average order value',
    paymentSuccess: 'Payment success rate', ofAttempts: '{succeeded} of {attempted}',
    returnRate: 'Return rate', refunded: '{count} returns · {amount} refunded',
    onlineSales: 'Online sales', branchSales: 'Branch sales',
    activeCustomers: 'Active customers', newCustomers: '{count} new',
    agentOrdersHour: 'Agent orders / hour', lastRun: 'last run {status}',
    dailySales: 'Daily sales', channelMix: 'Channel mix', salesByBranch: 'Sales by branch',
    bestProducts: 'Best-selling products', lowStock: 'Low stock',
    platformActivity: 'Platform activity',
    noSales: 'No sales in this period yet.', noRevenue: 'No revenue recorded yet.',
    noBranchSales: 'No branch sales yet.',
    product: 'Product', units: 'Units', revenue: 'Revenue', warehouse: 'Warehouse',
    available: 'Available', reorderAt: 'Reorder at', lines: '{count} line(s)',
    runsCompleted: 'Agent runs completed', runsFailed: 'Agent runs failed',
    runsLastHour: 'Runs in the last hour', aiAnswers: 'AI answers given',
    aiLatency: 'Average AI response', aiVerified: 'Answers verified against source',
    noModelWarning:
      'No OpenAI key is configured on this deployment. Answers still retrieve and compute ' +
      'real figures, but are returned without model narration.',
    autoRefresh: 'Figures refresh automatically while this page is open.',
  },
  analytics: {
    title: 'Analytics',
    subtitle: 'Derived from invoices — money actually collected, not orders placed.',
    range7: '7 days', range30: '30 days', range90: '90 days', range365: '12 months',
    vatCollected: 'VAT collected', discountsGiven: 'Discounts given', ordersPlaced: 'Orders placed',
    cancelled: 'Cancelled', paymentFailures: 'Payment failures',
    customersBought: 'Customers who bought', ofActive: 'of {count} active',
    revenueOverTime: 'Revenue over time', branchPerformance: 'Branch performance',
    returnsByReason: 'Returns by reason', highestValue: 'Highest lifetime value',
    noReturns: 'No returns in this period.', noPeriodSales: 'No sales in this period.',
    branch: 'Branch', invoicesCol: 'Invoices', sales: 'Sales', avgOrder: 'Avg order',
    customer: 'Customer', type: 'Type', city: 'City', lifetimeValue: 'Lifetime value',
  },
  agent: {
    title: 'Shopping Agent',
    subtitle: 'Generates realistic business activity against the Simulator API.',
    startRun: 'Start run', noScenario: 'No scenario (baseline)',
    runsCompleted: 'Runs completed', runsFailed: 'Runs failed',
    ordersLast50: 'Orders (last 50 runs)', valueGenerated: 'Value generated',
    schedulerDisabled:
      'Scheduled runs are disabled on this deployment (AGENT_RUNNER_ENABLED=false). ' +
      'Manual runs started here still work.',
    configurations: 'Configurations', recentRuns: 'Recent runs',
    name: 'Name', schedule: 'Schedule', scenario: 'Scenario', lastRun: 'Last run',
    run: 'Run', status: 'Status', orders: 'Orders', value: 'Value',
    decisionLog: 'Decision log', decisionLogPrompt: 'Decision log — select a run above',
    decisionLogEmpty: 'Every decision the agent made is recorded',
    decisionLogHint:
      'Choose a run to see which persona it drew, which products it chose, and why any ' +
      'payment failed.',
    loadingDecisions: 'Loading decisions', stopRun: 'Stop run {run}', retryRun: 'Retry run {run}',
    actionFailed: 'The agent rejected that action', default: 'default',
    unreachable: 'Could not reach the Shopping Agent service',
  },
  connectors: {
    title: 'Data Connectors',
    subtitle:
      'The isolation layer between Nuat AI and business systems. Everything the AI reads ' +
      'passes through here.',
    unreachable: 'Could not reach the Data Connector service',
    testConnection: 'Test connection', testing: 'Testing…', syncSchema: 'Sync schema',
    syncing: 'Syncing…', lastTested: 'Last tested', lastSynced: 'Last synced',
    noneTitle: 'No connectors registered',
    noneBody: 'Register a source so Nuat AI has something to read.',
    actionFailed: 'The connector rejected that action',
    allowlistTitle: 'Allowlisted queries ({count})',
    allowlistNote:
      'Nuat AI can only run these. Anything not listed cannot be retrieved — there is no ' +
      'path from model output to an arbitrary query.',
    personalFiltered: 'personal data filtered',
    auditTitle: 'Retrieval audit', query: 'Query', recordsCol: 'Records',
    outcome: 'Outcome', when: 'When',
  },
  records: {
    products: 'Products', productsSubtitle: 'The simulator catalogue, with live stock levels.',
    customers: 'Customers',
    customersSubtitle: 'Every customer here is synthetic — no real personal data is stored.',
    orders: 'Orders', ordersSubtitle: 'Sales orders across every channel.',
    invoices: 'Invoices', invoicesSubtitle: 'The financial record behind every completed sale.',
    payments: 'Payments', paymentsSubtitle: 'Payment attempts, including declines.',
    returns: 'Returns', returnsSubtitle: 'Return requests and their refunds.',
    users: 'Users & roles', usersSubtitle: 'Platform accounts and what each one may do.',
    audit: 'Audit logs',
    auditSubtitle: 'Append-only record of every state-changing operation in the simulator.',
    colProduct: 'Product', colCategory: 'Category', colPrice: 'Price', colStock: 'Stock',
    colStatus: 'Status', colCustomer: 'Customer', colType: 'Type', colCity: 'City',
    colPersona: 'Persona', colCreated: 'Created', colOrder: 'Order', colChannel: 'Channel',
    colTotal: 'Total', colPlaced: 'Placed', colInvoice: 'Invoice', colIssued: 'Issued',
    colReference: 'Reference', colMethod: 'Method', colAmount: 'Amount', colRefunded: 'Refunded',
    colWhen: 'When', colReturn: 'Return', colReason: 'Reason', colRefund: 'Refund',
    colRaised: 'Raised', colUser: 'User', colRoles: 'Roles', colLastSignIn: 'Last sign-in',
    colAction: 'Action', colEntity: 'Entity', colActor: 'Actor', colRequest: 'Request',
    active: 'Active', inactive: 'Inactive', disabled: 'Disabled', synthetic: 'synthetic',
    mustChangePassword: 'must change password', agentGenerated: 'agent-generated',
    agentRun: 'agent run', system: 'system',
    filterStatus: 'Status', filterChannel: 'Channel', filterType: 'Type',
    filterMethod: 'Method', filterEntity: 'Entity',
  },
  health: {
    title: 'System health',
    subtitle: 'Live probe of every backend service and its dependencies.',
    healthy: 'healthy', degraded: 'degraded', unreachable: 'unreachable',
    roundTrip: 'Round trip', uptime: 'Uptime', environment: 'Environment',
    noResponse: 'No response', reprobed: 'Re-probed every 15 seconds while this page is visible.',
    probing: 'Probing services', up: 'up', down: 'down',
    units: { second: 's', minute: 'm', hour: 'h', day: 'd' },
  },
};

const ar: Dictionary = {
  app: { name: 'إدارة نوات', signIn: 'تسجيل الدخول', signOut: 'تسجيل الخروج', language: 'اللغة' },
  auth: {
    title: 'تسجيل الدخول',
    subtitle: 'استخدم حسابك على منصة نوات.',
    email: 'البريد الإلكتروني',
    password: 'كلمة المرور',
    showPassword: 'إظهار كلمة المرور',
    hidePassword: 'إخفاء كلمة المرور',
    signingIn: 'جارٍ تسجيل الدخول…',
    devAccounts: 'حسابات التطوير',
    devNote: 'تظهر لأن التطبيق يعمل على localhost.',
    failed: 'فشل تسجيل الدخول',
    heroTitle: 'نواة بيانات شركتك',
    heroBody:
      'منفتحة حيث ينبغي، ومحميّة حيث يجب. وكل رقم تعرضه المنصة يمكن تتبّعه إلى السجل الذي جاء منه.',
    arabicTagline: 'نواة بياناتك',
  },
  nav: {
    overview: 'نظرة عامة', dashboard: 'اللوحة', analytics: 'التحليلات',
    simulator: 'المحاكي', products: 'المنتجات', customers: 'العملاء', orders: 'الطلبات',
    invoices: 'الفواتير', payments: 'المدفوعات', returns: 'المرتجعات',
    automation: 'الأتمتة والبيانات', agent: 'وكيل التسوّق', connectors: 'موصّلات البيانات',
    governance: 'الحوكمة', users: 'المستخدمون والأدوار', audit: 'سجلات التدقيق',
    system: 'صحّة النظام',
    sections: 'الأقسام', open: 'فتح القائمة', close: 'إغلاق القائمة',
  },
  common: {
    refresh: 'تحديث', refreshNow: 'تحديث الآن', retry: 'إعادة المحاولة', loading: 'جارٍ التحميل',
    search: 'بحث…', previous: 'السابق', next: 'التالي', pageOf: 'صفحة {page} من {total}',
    records: '{count} سجل', all: 'الكل', nothingToShow: 'لا يوجد ما يُعرض.',
    readOnly: 'للقراءة فقط — لا تملك صلاحية {permission}', unavailable: 'غير متاح',
    errorTitle: 'حدث خطأ ما', restoringSession: 'جارٍ استعادة الجلسة',
    noResults: 'لا توجد {entity}',
    adjustFilters: 'عدّل المرشّحات، أو ابدأ تشغيلًا للوكيل لتوليد نشاط.',
  },
  dashboard: {
    title: 'اللوحة التنفيذية',
    subtitle: 'آخر 30 يومًا · من {from} إلى {to}',
    totalSales: 'إجمالي المبيعات', invoicesCount: '{count} فاتورة',
    averageOrderValue: 'متوسّط قيمة الطلب',
    paymentSuccess: 'معدّل نجاح الدفع', ofAttempts: '{succeeded} من {attempted}',
    returnRate: 'معدّل الإرجاع', refunded: '{count} مرتجع · رُدّ {amount}',
    onlineSales: 'مبيعات أونلاين', branchSales: 'مبيعات الفروع',
    activeCustomers: 'عملاء نشطون', newCustomers: '{count} جديد',
    agentOrdersHour: 'طلبات الوكيل / ساعة', lastRun: 'آخر تشغيل {status}',
    dailySales: 'المبيعات اليومية', channelMix: 'توزيع القنوات', salesByBranch: 'المبيعات حسب الفرع',
    bestProducts: 'الأكثر مبيعًا', lowStock: 'مخزون منخفض',
    platformActivity: 'نشاط المنصة',
    noSales: 'لا مبيعات في هذه الفترة بعد.', noRevenue: 'لم تُسجَّل إيرادات بعد.',
    noBranchSales: 'لا مبيعات فروع بعد.',
    product: 'المنتج', units: 'الوحدات', revenue: 'الإيراد', warehouse: 'المستودع',
    available: 'المتاح', reorderAt: 'حدّ إعادة الطلب', lines: '{count} صنف',
    runsCompleted: 'تشغيلات مكتملة', runsFailed: 'تشغيلات فاشلة',
    runsLastHour: 'تشغيلات آخر ساعة', aiAnswers: 'إجابات الذكاء الاصطناعي',
    aiLatency: 'متوسّط زمن الاستجابة', aiVerified: 'إجابات مُتحقَّق منها مقابل المصدر',
    noModelWarning:
      'لم يُضبط مفتاح OpenAI في هذه النسخة. لا تزال الإجابات تسترجع أرقامًا حقيقية وتحسبها، ' +
      'لكنها تُعرض دون صياغة من النموذج.',
    autoRefresh: 'تُحدَّث الأرقام تلقائيًّا طالما هذه الصفحة مفتوحة.',
  },
  analytics: {
    title: 'التحليلات',
    subtitle: 'مستخرجة من الفواتير — الأموال المحصّلة فعلًا، لا الطلبات المُسجَّلة.',
    range7: '7 أيام', range30: '30 يومًا', range90: '90 يومًا', range365: '12 شهرًا',
    vatCollected: 'ضريبة القيمة المضافة المحصّلة', discountsGiven: 'الخصومات الممنوحة',
    ordersPlaced: 'طلبات مُسجَّلة', cancelled: 'ملغاة', paymentFailures: 'مدفوعات فاشلة',
    customersBought: 'عملاء اشتروا', ofActive: 'من {count} نشط',
    revenueOverTime: 'الإيرادات عبر الزمن', branchPerformance: 'أداء الفروع',
    returnsByReason: 'المرتجعات حسب السبب', highestValue: 'الأعلى قيمة دائمة',
    noReturns: 'لا مرتجعات في هذه الفترة.', noPeriodSales: 'لا مبيعات في هذه الفترة.',
    branch: 'الفرع', invoicesCol: 'الفواتير', sales: 'المبيعات', avgOrder: 'متوسّط الطلب',
    customer: 'العميل', type: 'النوع', city: 'المدينة', lifetimeValue: 'القيمة الدائمة',
  },
  agent: {
    title: 'وكيل التسوّق',
    subtitle: 'يُنتج نشاطًا تجاريًّا واقعيًّا عبر واجهة المحاكي البرمجية.',
    startRun: 'ابدأ تشغيلًا', noScenario: 'بدون سيناريو (الوضع الأساسي)',
    runsCompleted: 'تشغيلات مكتملة', runsFailed: 'تشغيلات فاشلة',
    ordersLast50: 'الطلبات (آخر 50 تشغيلًا)', valueGenerated: 'القيمة المُولَّدة',
    schedulerDisabled:
      'التشغيلات المجدولة معطّلة في هذه النسخة (AGENT_RUNNER_ENABLED=false). ' +
      'ولا تزال التشغيلات اليدوية من هنا تعمل.',
    configurations: 'الإعدادات', recentRuns: 'أحدث التشغيلات',
    name: 'الاسم', schedule: 'الجدولة', scenario: 'السيناريو', lastRun: 'آخر تشغيل',
    run: 'التشغيل', status: 'الحالة', orders: 'الطلبات', value: 'القيمة',
    decisionLog: 'سجل القرارات', decisionLogPrompt: 'سجل القرارات — اختر تشغيلًا بالأعلى',
    decisionLogEmpty: 'كل قرار اتّخذه الوكيل مُسجَّل',
    decisionLogHint:
      'اختر تشغيلًا لترى أي شخصية اختارها، وأي منتجات وقع عليها الاختيار، ولماذا فشلت أي دفعة.',
    loadingDecisions: 'جارٍ تحميل القرارات', stopRun: 'إيقاف التشغيل {run}',
    retryRun: 'إعادة التشغيل {run}',
    actionFailed: 'رفض الوكيل هذا الإجراء', default: 'افتراضي',
    unreachable: 'تعذّر الوصول إلى خدمة وكيل التسوّق',
  },
  connectors: {
    title: 'موصّلات البيانات',
    subtitle:
      'طبقة العزل بين ذكاء نوات وأنظمة الأعمال. كل ما يقرؤه الذكاء الاصطناعي يمرّ من هنا.',
    unreachable: 'تعذّر الوصول إلى خدمة موصّل البيانات',
    testConnection: 'اختبار الاتصال', testing: 'جارٍ الاختبار…', syncSchema: 'مزامنة المخطّط',
    syncing: 'جارٍ المزامنة…', lastTested: 'آخر اختبار', lastSynced: 'آخر مزامنة',
    noneTitle: 'لا توجد موصّلات مسجّلة',
    noneBody: 'سجّل مصدرًا ليكون لدى ذكاء نوات ما يقرؤه.',
    actionFailed: 'رفض الموصّل هذا الإجراء',
    allowlistTitle: 'الاستعلامات المُصرّح بها ({count})',
    allowlistNote:
      'لا يستطيع ذكاء نوات تشغيل غيرها. وما ليس مدرجًا لا يمكن استرجاعه — ولا مسار من ' +
      'مخرجات النموذج إلى استعلام عشوائي.',
    personalFiltered: 'البيانات الشخصية مُرشَّحة',
    auditTitle: 'تدقيق الاسترجاع', query: 'الاستعلام', recordsCol: 'السجلات',
    outcome: 'النتيجة', when: 'الوقت',
  },
  records: {
    products: 'المنتجات', productsSubtitle: 'كتالوج المحاكي، مع مستويات المخزون الحيّة.',
    customers: 'العملاء',
    customersSubtitle: 'كل عميل هنا اصطناعي — لا تُخزَّن أي بيانات شخصية حقيقية.',
    orders: 'الطلبات', ordersSubtitle: 'طلبات البيع عبر كل القنوات.',
    invoices: 'الفواتير', invoicesSubtitle: 'السجل المالي وراء كل عملية بيع مكتملة.',
    payments: 'المدفوعات', paymentsSubtitle: 'محاولات الدفع، بما فيها المرفوضة.',
    returns: 'المرتجعات', returnsSubtitle: 'طلبات الإرجاع ومبالغ الاسترداد.',
    users: 'المستخدمون والأدوار', usersSubtitle: 'حسابات المنصة وما يُسمح به لكلٍّ منها.',
    audit: 'سجلات التدقيق',
    auditSubtitle: 'سجل لا يُعدَّل لكل عملية تغيّر الحالة في المحاكي.',
    colProduct: 'المنتج', colCategory: 'الفئة', colPrice: 'السعر', colStock: 'المخزون',
    colStatus: 'الحالة', colCustomer: 'العميل', colType: 'النوع', colCity: 'المدينة',
    colPersona: 'الشخصية', colCreated: 'أُنشئ', colOrder: 'الطلب', colChannel: 'القناة',
    colTotal: 'الإجمالي', colPlaced: 'سُجِّل', colInvoice: 'الفاتورة', colIssued: 'صدرت',
    colReference: 'المرجع', colMethod: 'الوسيلة', colAmount: 'المبلغ', colRefunded: 'المُسترد',
    colWhen: 'الوقت', colReturn: 'المرتجع', colReason: 'السبب', colRefund: 'الاسترداد',
    colRaised: 'قُدِّم', colUser: 'المستخدم', colRoles: 'الأدوار', colLastSignIn: 'آخر دخول',
    colAction: 'الإجراء', colEntity: 'الكيان', colActor: 'الفاعل', colRequest: 'الطلب',
    active: 'نشط', inactive: 'غير نشط', disabled: 'معطّل', synthetic: 'اصطناعي',
    mustChangePassword: 'يجب تغيير كلمة المرور', agentGenerated: 'من إنتاج الوكيل',
    agentRun: 'تشغيل الوكيل', system: 'النظام',
    filterStatus: 'الحالة', filterChannel: 'القناة', filterType: 'النوع',
    filterMethod: 'الوسيلة', filterEntity: 'الكيان',
  },
  health: {
    title: 'صحّة النظام',
    subtitle: 'فحص حيّ لكل خدمة خلفية وتبعيّاتها.',
    healthy: 'سليمة', degraded: 'متدهورة', unreachable: 'غير متاحة',
    roundTrip: 'زمن الرحلة', uptime: 'مدّة التشغيل', environment: 'البيئة',
    noResponse: 'لا استجابة', reprobed: 'يُعاد الفحص كل 15 ثانية طالما الصفحة ظاهرة.',
    probing: 'جارٍ فحص الخدمات', up: 'تعمل', down: 'متوقّفة',
    units: { second: 'ث', minute: 'د', hour: 'س', day: 'ي' },
  },
};

const DICTIONARIES: Record<Locale, Dictionary> = { en, ar };

export const getDictionary = (locale: Locale): Dictionary => DICTIONARIES[locale] ?? DICTIONARIES.en;
